import { config } from '@/config'
import { service, datatrans } from '@/service/index'
import urlWatcher from '@/helpers/urlwatcher'
import { format } from '@/utils/format'
import dayjs, { setLocalTimezone, formatServer, dayjsLocal, dayjsServer } from '@/helpers/dayjs'
import Vue from 'vue'
import scrrroll from "scrrroll"
import {eventhub, EVENT_ACCOUNT_ISLOGGEDIN, EVENT_CART_TIMECHANGED, EVENT_CART_CREATED, EVENT_SHOP_INITIALIZED} from './eventhub'
import getters from './getters'
import pushGTM from '@/utils/pushGTM'
import { getUrlParams } from '@/utils/urlParams';
import loadScript from '@/utils/loadscript'
import { switchDateFormat } from '@/utils/formatDate'
import { getGtmConsentCategories } from '@/helpers/TrackingHelpers'

let initializePromise = null
let submitTimeout = null

export default {
	async initialize({dispatch, state, commit, getters}) {
		if (initializePromise) {
			return initializePromise
		}
		config.DEBUG && console.info("Initialize...")
		let initializePromiseResolve
		initializePromise = new Promise((res, rej) => {
			initializePromiseResolve = res
		})

		// DEBUG: this should be early enough to just overwrite the config
		let params = urlWatcher.readUrl()
		if (params.mock != null) {
			config.MOCK = params.mock == "true"
			window.localStorage.setItem(config.MOCK_COOKIE, config.MOCK.toString())
		}

    // restore user if cookie is existent
    await dispatch('account/restoreUser')

		if (params.loadOrder) {
			await dispatch('order/load', { token: params.loadOrder })
		}

		// try restoring group order participant
		await dispatch('grouporder/restoreParticipant')

		//get both infos, after this is we know if there is a valid basket and a valid user
		const { tokenValue: token } = getUrlParams();
		const accumulatorGroup = "initialize"
		let [existsResult] = await Promise.all([
			dispatch('cart/exists', { token, detached: !!token, accumulatorGroup })
				.catch(e => Promise.resolve(e)),
			// dispatch('getBrands', { accumulatorGroup }),
		])

		let basketToken = null
		if (['shop', 'checkout',].includes(getters.shopArea)) {
			basketToken = getters['cart/tokenValue']
		}
    const async = [
      dispatch('account/getUser', { basketToken, accumulatorGroup })
      .catch(e => Promise.resolve(e))
    ]

    if (getters['productlist/useMenu'] && getters.shopArea !== 'shop') {
      async.push(
        dispatch('childproducts/loadCategories', { accumulatorGroup })
      )
    }

		await Promise.all(async)

		if (config.MOCK && config.MOCK_LOGIN) {
			//submit some mock data for the account
			await dispatch('login', {email: "fake@mock.ch", password: "blub"})
			await dispatch('account/loadQRToken')
			console.log('QRCODE: ', `${config.ACCOUNT_QR_PREFIX}${getters['account/accountQrToken']}`)
		}

		if (config.LOCAL_TIME) {
			setLocalTimezone(config.LOCAL_TIME.substring(config.LOCAL_TIME.length - 6))
		}

		commit('setInitialized', { item: true })

		setTimeout(initializePromiseResolve, 0)
		return initializePromise
	},
	async initializeShoplist({ dispatch, state, commit, getters }, { menuMode, menuChannel, menuTimedAvailability } = {}) {
		commit('setShopArea', { item: 'shop' })
		await dispatch('productlist/setMenuMode', { menuMode, menuChannel, menuTimedAvailability })
		await dispatch('initialize')
		await dispatch('cart/showContinue', !!menuMode);

		try {
			if (service.mock) {
				await dispatch('debugFillCart')
			}

			await Promise.all([
				dispatch('updateShoplist'),
				dispatch('productlist/loadNextProductAvailablityChangeTime', { accumulatorGroup: 'productListCategories' }),
			])
		} catch(err) {
      config.DEBUG && console.error(err)
			throw err
		}

		eventhub.$emit(EVENT_SHOP_INITIALIZED)
	},

	async updateShoplist({ dispatch, state, getters, commit }) {
		commit('setShopArea', { item: 'shop' });
		const favoriteCode = getters['productlist/favoriteCode']
    let { publicChannel: channel } = getUrlParams()
		await Promise.all([
			dispatch('productlist/loadCategories', { channel, brand: config.BRAND, accumulatorGroup: 'productListCategories' }),
			dispatch('productlist/readFilters', { accumulatorGroup: 'productListCategories' }),
		])

		let params = urlWatcher.readUrl()

		//read filters form html

		//set filters active
		if (params.filters) {
			params.filters
				.split(",")
				.forEach(v => dispatch('productlist/setFilter', {filterId: v, active: true}))
		}

		let accumulatorGroup = "productList"
		let asyncQueue = []
    if (getters['productlist/useMenu']) {
      asyncQueue.push(dispatch('childproducts/loadProducts', { accumulatorGroup }))
    }
		if (getters['account/isLoggedIn']) {
			let channel = state.cart.serverData ? state.cart.serverData.channel.code : null
			let shippingCode = (getters['cart/shippingMethod'] || {}).code
			asyncQueue.push(dispatch('account/loadLastOrders', { accumulatorGroup, channel, shippingCode }))
			asyncQueue.push(
				dispatch('account/loadFavoriteProducts', { accumulatorGroup })
				.catch(err => {
						// HACK: currently this endpoint does not work, so we resolve
					config.DEBUG && console.warn('account/loadFavoriteProducts endpoint does not function (yet), fallback')
					return Promise.resolve([])
				})
			)
		}

    if (!getters['productlist/useMenu']) {
      let overviewTaxon

      if (params.taxon) {
        overviewTaxon = await dispatch('productlist/findOverviewTaxon', params.taxon);

        if (overviewTaxon) {
          commit('productlist/setActiveCategory', { item: overviewTaxon.code })
        }
      }

      asyncQueue.push(dispatch('productlist/loadProductList', { channel, accumulatorGroup }))
    }

		// fetch the whole queue
		return Promise.all(asyncQueue)
	},

	async initializeSidebarCart({ dispatch, state }) {
		await dispatch('initialize')
	},

	async initializeOrderbox({ dispatch, state, commit }) {
		commit('location/setLoading', { item: true }); // do this here to show the order box loader right away

		const accumulatorGroup = 'orderbox'
		let asyncQueue = [
			dispatch('initialize'),
			dispatch('location/getLocations', { accumulatorGroup }),
		]

		try {
			await Promise.all(asyncQueue)
		} catch(err) {
			// ¯\_(ツ)_/¯
		}
	},

  async setGeolocation({commit}, value) {
		commit('setGeolocation', { item: value });
	},

	async clearGeolocation({commit}) {
		commit('setGeolocation', { item: null });
	},

	async debugFillCart({state, dispatch}) {
		//HACK: DEBUG CODE
		if (!state.cart.basketToken || state.cart.items.length < 1) {
			if (config.MOCK_CARTOPEN_FAILONCE) { // we have to deactivate this mock fail for the test
				tmpService.fakeFailOpenOnce = false
			}
			await dispatch('cart/open', { channel: "butcher_zurich_niederdorf_1" })
			if (['kitchenrepublic', 'nooch', 'negishi'].includes(config.BRAND)) {
				await dispatch('cart/addItems', { items: [
					{ // Edamame
						"quantity": 1,
						"productCode": "10201001",
						"variantCode": "10201001"
					},
					{ // New Generation - Plant-based Uramaki Thonmousse - 8 stk
						"quantity": 1,
						"productCode": "10941032",
						"variantCode": "10941031"
					}
				]})
			}
			if (['kitchenrepublic', 'butcher'].includes(config.BRAND)) {
				await dispatch('cart/addItems', { items: [
					{ // Fresh Butcher Fries (maxi)
						"quantity": 1,
						"productCode": "13001001",
						"variantCode": "13001001-variant-217",
						"forceCopy": true
					},
					{ // Burger copy product
						"quantity": 1,
						"productCode": "13001001",
						"variantCode": "13001001-variant-217",
						"childCodes": [
							"18003001"
						],
						"forceCopy": true
					}
				]})
				// change the child prodcuts to butcher dip and fav sauce
				await dispatch('childproducts/adjustChildProducts', {
					item: state.cart.items.filter(item => item.productCode == '13001001')[1],
					childCodes: ["18003001", "18003002"]
				})
			}
			if (['kitchenrepublic'].includes(config.BRAND)) {
				await dispatch('cart/addItems', {
					items: [
						{ // Poke Classic Salmon - Nerone Reis - ohne Tomaten
								"quantity": 1,
								"productCode": "15001001",
								"variantCode": "15001001",
								"childCodes": [
										"18031006",
										"14818951789158"
								],
								"forceCopy": true
						}
					]
				})
			}

			await dispatch('cart/addCoupon', {coupon: "test"})
		}
	},

	async initializeCheckout({ dispatch, state, getters, commit }) {
		commit('setShopArea', { item: 'checkout' })
		await dispatch('initialize')
    await dispatch('account/getUserCoupons', { basketToken: getters['cart/tokenValue']})
		await dispatch('cart/randomizeTipVariant')

		if (!state.cart.basketToken && state.order.basketToken) {
			dispatch('redirect', { name: "checkout-complete", params: { basketToken: state.order.basketToken, orderType: getters['order/orderType'] }})
			return
		}

		await dispatch('cart/showContinue', false);
		if (config.MOCK) {
			for (let type of ['', 'info', 'promo', 'success', 'warning', 'error']) {
				dispatch('notification/addMessage', {
					message: {
						"type": type,
						"message": type ? `This is a message of type: "${type}"` : 'This is a message with no type',
					}
				})
			}
		}

		//add messages from html
		if (config.messages) {
			config.messages
				.forEach(message => dispatch('notification/addMessage', { message }))
		}

		try {
			const accumulatorGroup = "initialize"

			let asyncs = [
				dispatch('checkout/payments', {accumulatorGroup}),
				dispatch('checkout/summary', {accumulatorGroup})
			]
			if (getters['account/isLoggedIn']) {
				asyncs.push(dispatch('checkout/savedPayments', {accumulatorGroup}))
				asyncs.push(dispatch('account/loadAddresses', {accumulatorGroup}))
			}

			await Promise.all(asyncs)

			if (service.mock) {
				await dispatch('debugFillCart')
			}
		} catch(err) {
			config.DEBUG && console.error("Checkout::initialize -> redirect home", err)
			dispatch('redirect', {name: "home"})
		}

	},

	async initializeCheckoutSuccess({ dispatch, state, getters, commit }, {token = null} = {}) {
		commit('setShopArea', { item: 'checkout-success' })
		await dispatch('initialize')
		await dispatch('cart/clearTipVariant')
		// await dispatch('cart/showContinue', false);

		//add messages from html
		if (config.messages) {
			config.messages
				.forEach(message => dispatch('notification/addMessage', { message }))
		}
	},

	async initializeAccount({ dispatch, state, getters, commit }) {
		commit('setShopArea', { item: 'account' })
		await dispatch('initialize')

		const accumulatorGroup = "initialize"

		if (getters['account/isLoggedIn']) {
			let asyncs = [
        dispatch('account/getUserCoupons', {accumulatorGroup}),
				dispatch('account/loadAddresses', {accumulatorGroup}),
				dispatch('account/loadLastOrders', { limit: 10, accumulatorGroup }),
				dispatch('account/loadReservations', { limit: 10, accumulatorGroup }),
			]

			await Promise.all(asyncs)
		}
	},

	async openCartAtChannel({dispatch, state, getters}, {noRedirect = false, channel, zip, shippingCode, orderNow, deliverDate, deliverTime, alternativeBrand = false, replaceOldCart = false, referralToken = null, reservationHash = null, reservationUser = null, outdoorDeliveryId = null}) {
		let orderTime;

		// HACK: we are not allowed to send orderNow to the service, so it cant be a normal boolean, same is for ordertime
		if (orderNow) {
			orderTime = null
		}

		if (!orderNow) {
			let spl = deliverTime.split(":")
			orderTime = dayjsLocal(deliverDate).hour(spl[0]).minute(spl[1]).second(0)
		}

		const location = getters['location/location'];
		pushGTM({
			event: shippingCode,
			restaurantLocation: location?.name || '',
			restaurantChannel: location?.code || channel,
			brand: config.BRAND,
 			plz: zip || null,
		});

		await dispatch('grouporder/clearGroupOrder')

		const basketToken = await dispatch('cart/open', {
			channel,
			zip,
			shippingCode,
			orderNow,
			deliverTime: orderTime ? formatServer(orderTime) : null,
			oldToken: replaceOldCart && state.cart.basketToken ? state.cart.basketToken : undefined,
			referralToken,
			alternativeBrand,
			reservationHash,
			reservationUser,
			outdoorDeliveryId,
		})

		eventhub.$emit(EVENT_CART_CREATED);

		if (!noRedirect) {
			const redirected = await dispatch('redirect', {name: "shop"});

			if (!redirected) {
				await dispatch('updateShoplist');
			}
		}
		return basketToken
	},

  async setShippingAddress({ dispatch, state, getters }, { form }) {
		let shippingAddress = {
			...form.shippingAddress,
			countryCode: form.shippingAddress.countryCode
				|| config.COUNTRY
		}

		await Promise.all([
			dispatch('checkout/addressChange', {
				email: form.email,
				shippingAddress,
				accumulatorGroup: 'setAddress'
			}),
			dispatch('cart/load', { accumulatorGroup: 'setAddress' })
		])
	},

	async submitOrder({ dispatch, state, getters }, { form, savedPaymentToken }) {
    return new Promise(async (resolve, reject) => {
      try {
        const orderStates = getters['cart/orderStates'];
        clearTimeout(submitTimeout)

        let order = {
          email: form.email,
          notes: form.notes,
          confirmedAlcohol: form.checkAlcohol,
          tip: form.tip ? form.tip : null, // HACK: yes, the api wants a null if its 0
          compensateCo2: form.compensateCo2,
          useDishes: form.useDishes,
          dropoffType: form.dropoffType,
          subscribedToNewsletter: form.subscribedToNewsletter,
          acceptTerms: form.acceptTerms,
          acceptPrivacy: form.acceptPrivacy,
          gtmConsent: getGtmConsentCategories(),
        }
        if (order.useDishes) {
          order.dishReturnMethod = form.dishReturnMethod
        }

        if (getters["account/isLoggedIn"]) {
          order.userToken = state.account.token
        } else if (form.createUser) {
          order.createUser = true
        }

        //we add the countrycode from config if it is not provided
        let shippingAddress = {
          ...form.shippingAddress,
          countryCode: form.shippingAddress.countryCode
            || config.COUNTRY
        }
        let billingAddress = {
          ...form.billingAddress,
          countryCode: form.billingAddress.countryCode
            || config.COUNTRY
        }

        //we duplicate the shipping to billing if its empty
        //TODO: this check has some fail cases and it should not be required to check both
        if (!form.billingAddress.firstName || !form.billingAddress.lastName) {
          billingAddress = {
            ...billingAddress,
            ...shippingAddress
          }
        }

        submitTimeout = setTimeout(() => {
          const error = new Error('Shopmanager complete has timed out.')
          error.type = 'timeout'
          reject(error)
        }, config.THEME_PAYMENT_TIMEOUT)

        //prepare async opts
				const accumulatorGroup = 'orderSubmit';
        let paymentAsync = Promise.resolve(true)
        let addressAsync = Promise.resolve(true)

        if (
          !['locked', 'disabled'].includes(orderStates.shippingAddressState)
        ) {
          addressAsync = dispatch('checkout/addressChange', {
              billingAddress,
              shippingAddress,
              email: form.email,
							accumulatorGroup,
            })
            .catch(err => {
              config.DEBUG && console.error(err)
              return Promise.resolve({error: true, ...err})
            })
        }


        if (!['locked', 'disabled'].includes(orderStates.paymentState)) {
          // check if there are available selectable payments, else we do have a zero Payment
          // TODO: this is an paradigm, it has to be written another way
          if (getters['checkout/checkoutPayments'].length) {
            paymentAsync = dispatch('checkout/setPayment', { method: form.paymentCode, accumulatorGroup })
              .catch(err => {
                config.DEBUG && console.error(err)
                return Promise.resolve({error: true, ...err})
              })
          }
        }

        let submitAsync = dispatch('checkout/submit', {
          ...order,
          accumulatorGroup
        })
          .catch(err => {
            config.DEBUG && console.error(err)
            return Promise.resolve({error: true, ...err})
          })

        let [addressResult, paymentResult, submitResult] = await Promise.all([addressAsync, paymentAsync, submitAsync])
        clearTimeout(submitTimeout)
        config.DEBUG && console.info("results:", submitResult, addressResult, paymentResult)
        if (addressResult && addressResult.error) {
          //address error happend
          throw addressResult
        } else if (submitResult && submitResult.error) {
          //submit error happend
          // short inline update of the cart
          await dispatch('cart/load')

          throw submitResult
        } else {
          if (state.checkout.checkoutToken) {

            submitTimeout = setTimeout(() => {
              const error = new Error('Shopmanager load order has timed out.')
              error.type = 'timeout'
              reject(error)
            }, config.THEME_PAYMENT_TIMEOUT)

            const order = await dispatch('checkout/order', { accumulatorGroup })
            clearTimeout(submitTimeout)

            const payment = order.payments.find(payment => payment.state === 'new')

            if (order.payments.length > 0 && payment) {
              //TODO: detect datatrans other wayy
              // HACK: this is mapped on the original paymentmethods, but this is a check on a copy
              if (config.CHECKOUT_OFFLINEPAYMENT_IDS.includes(payment.method.code)) {
                //TODO: if we land here, we have to redirect to abort
                dispatch('redirect', {
                  name: "checkout-complete",
                  // url: format(config.CHECKOUT_COMPLETE_URL, {basketToken: order.tokenValue}),
                  params: {basketToken: order.tokenValue, orderType: order.orderType}
                })
              } else if (payment.method.code === 'wallee' || payment.method.walleePaymentMethodConfigurationId) {
                submitTimeout = setTimeout(() => {
                  const error = new Error('Wallee payment has timed out.')
                  error.type = 'timeout'
                  throw error
                }, config.THEME_PAYMENT_TIMEOUT)

                await dispatch('payWithWallee', {
                  order,
                  payment,
                  savedPaymentToken,
                })

                clearTimeout(submitTimeout)
              } else if (payment.method.code === 'datatrans' || payment.method.datatransPaymentMethod) {
                try {
                  submitTimeout = setTimeout(() => {
                    const error = new Error('Datatrans payment has timed out.')
                    error.type = 'timeout'
                    reject(error)
                  }, config.THEME_PAYMENT_TIMEOUT)

                  await dispatch('payWithDatatrans', {
                    order,
                    payment,
                    email: form.email,
                  })

                  clearTimeout(submitTimeout)
                } catch(err) {
                  if (err == "closed") {
                    dispatch('redirect', {
                      name: "checkout-cancel",
                      params: {basketToken: order.tokenValue}
                    })
                  }
                }
              } else {
                // hmmmm... something must be wrong
                const error = new Error('Payment did not match offline, wallee or datatrans')
                error.type = 'timeout'
                reject(error)
              }
            } else {
              // we didnt need payment, eg had voucher so we direct
              dispatch('redirect', {
                name: "checkout-complete",
                params: {basketToken: order.tokenValue, orderType: order.orderType}
              })
            }
          }
        }
      }
      catch(err) {
        clearTimeout(submitTimeout)
        reject(err)
      }
      clearTimeout(submitTimeout)
      resolve()
    })
	},

	async updateDeliverTime({ dispatch, state, getters }, { orderNow, date, time, confirmChange }) {
		var spl = time.split(":")
		// this code could be rewritten to dynamic time getter
		var orderTime = dayjsLocal(date).hour(spl[0]).minute(spl[1]).second(0)
		await dispatch('cart/updateDeliverTime', { orderNow, deliverTime: formatServer(orderTime), confirmChange })

		eventhub.$emit(EVENT_CART_TIMECHANGED)
	},

	async payWithWallee({ state, dispatch }, { order, payment, savedPaymentToken, redirects = {} } = {}) {
		const successUrl = format(redirects.checkoutComplete || config.CHECKOUT_COMPLETE_URL, {basketToken: order.tokenValue, orderType: order.orderType})
		const failedUrl = format(redirects.checkoutError || config.CHECKOUT_ERROR_URL, {basketToken: order.tokenValue});
		const data = {
			token: order.tokenValue,
			id: payment.id,
			successUrl,
			failedUrl
		};

		if (savedPaymentToken) {
			data.paymentToken = savedPaymentToken;
		}

		const transaction = await dispatch('checkout/transaction', data);
		await loadScript(transaction.javascriptUrl);

		window.LightboxCheckoutHandler.startPayment(transaction.walleePaymentMethodConfigurationId	?? 1, function() {
			alert('An error occurred during the initialization of the payment lightbox.');
		});
	},

	async payWithDatatrans({state, dispatch}, {order, payment, email, redirects = {}} = {}) {
		try {
			let currentPayment = payment || order.payments[0]
			let paymentData = {
				amount: currentPayment.price.current,
				ref: order.number,
				merchantId: order.datatransMerchantId || config.CHECKOUT_MERCHANT_ID,
				sign: order.datatransSign,
				checkoutComplete: format(redirects.checkoutComplete || config.CHECKOUT_COMPLETE_URL, {basketToken: order.tokenValue, orderType: order.orderType}),
				successUrl: format(redirects.checkoutSuccess || config.CHECKOUT_SUCCESS_URL, {basketToken: order.tokenValue}),
				cancelUrl: format(redirects.checkoutCancel || config.CHECKOUT_CANCEL_URL, {basketToken: order.tokenValue}),
				errorUrl: format(redirects.checkoutError || config.CHECKOUT_ERROR_URL, {basketToken: order.tokenValue}),
				additionalData: {
					language: config.LANGUAGE,
					uppCustomerDetails: "yes",
					uppCustomerTitle: "",
					uppCustomerFirstName: order.billingAddress?.firstName,
					uppCustomerLastName: order.billingAddress?.lastName,
					uppCustomerStreet: order.billingAddress?.street,
					uppCustomerStreet2: "",
					uppCustomerCity: order.billingAddress?.city,
					uppCustomerCountry: order.billingAddress?.countryCode,
					uppCustomerZipCode: order.billingAddress?.postcode,
					uppCustomerPhone: order.billingAddress?.phoneNumber,
					uppCustomerEmail: email,
					useAlias: true,
					uppRememberMe: "checked"
				}
			}
			if (currentPayment.method.datatransPaymentMethod) {
				paymentData.additionalData.paymentmethod = currentPayment.method.datatransPaymentMethod
			}

			let result = await datatrans.payment(paymentData)
		} catch(err) {
			if (config.MOCK && err == "closed" ) {
				alert("MOCK: Datatrans Closed: redirect would happen now")
				return
			}
			throw err
		}

	},

	async login({ state, dispatch, commit, getters }, { email, password, userGroup, userCoupon }) {
		try {
			const response = await dispatch('account/login', { email, password, userGroup, userCoupon, brand: config.BRAND })
			const accumulatorGroup = 'login'
			let channel = state.cart.serverData ? state.cart.serverData.channel.code : undefined
			let shippingCode = (getters['cart/shippingMethod'] || {}).code

			// if showContinue is true, we are outside of the shop/checkout
			// and therefor should not limit orders to channel or shippingCode
			if (getters['cart/showContinue']) {
				channel = null
				shippingCode = null
			}

			let basketToken = null
			if (['shop', 'checkout',].includes(getters.shopArea)) {
				basketToken = getters['cart/tokenValue']
			}

			await Promise.all([
				dispatch('account/getUser', { basketToken, accumulatorGroup }),
				dispatch('account/getUserCoupons', { basketToken, accumulatorGroup }),
				dispatch('account/loadLastOrders', { accumulatorGroup, channel, shippingCode }),
				// HACK: currently we preload addresses on every login to group over accumulator
				dispatch('account/loadAddresses', { accumulatorGroup }),
				dispatch('account/loadFavoriteProducts', { accumulatorGroup })
					.catch(err => {
						 // HACK: currently this endpoint does not work, so we resolve
						config.DEBUG && console.warn('account/loadFavoriteProducts endpoint does not function (yet), fallback')
						return Promise.resolve([])
					}),
			])

			eventhub.$emit(EVENT_ACCOUNT_ISLOGGEDIN)

			if (['checkout',].includes(getters.shopArea)) {
				dispatch('initializeCheckout');
			}
			else if (state.cart.basketToken) {
				dispatch('cart/load', { recalculate: true }).catch(err => {
					// do nothing
				});
			}

			return response;
		} catch(err) {
			throw err
		}
	},

	async logout({ state, dispatch, commit }) {
		dispatch('account/logout', { preserveStore: true })
		dispatch('redirect', { name: 'logout-after' })
	},

	async register({ commit, dispatch, state }, { email, password, firstName, lastName, phoneNumber, subscribedToNewsletter, checkTerms, userGroup, userCoupon, confirmUrl = ''}) {
		try {
			return await dispatch('account/register', { email, password, firstName, lastName, phoneNumber, subscribedToNewsletter, checkTerms, userGroup, userCoupon, registerConfirmUrl: confirmUrl || config.REGISTER_CONFIRM_URL, brand: config.BRAND })
		} catch(err) {
			throw err
		}
	},

	async redoOrder({ commit, dispatch, state }, { token }) {
		try {
			await dispatch('cart/restoreOrder', { token })
			await dispatch('cart/load')
		} catch(err) {
			config.DEBUG && console.error("could not restore", err)
		}
	},

	async poinzRegister({ commit, dispatch, state }, { birthday, gender }) {
		try {
			const accumulatorGroup = 'poinz-register';
			birthday = birthday.split('.').reverse().join('-');
			await Promise.all([
				dispatch('account/poinzRegister', { birthday, gender, accumulatorGroup }),
				dispatch('account/getUser', { accumulatorGroup })
			])
		} catch(err) {
			throw err
		}
	},

	async poinzRedeem({ commit, dispatch, state }) {
		try {
			const accumulatorGroup = 'poinz-redeem';
			await Promise.all([
				dispatch('account/poinzRedeem', { accumulatorGroup }),
				dispatch('account/getUser', { accumulatorGroup })
			])
		} catch(err) {
			throw err
		}
	},

  async subscribeNewsletter({}, { email, firstName, lastName, street, phoneNumber, city, postcode, birthday, terms, accumulatorGroup = null } = {}) {
    try {
      birthday = switchDateFormat(birthday, '-')
      return await service.subscribeNewsletter({ email, firstName, lastName, street, phoneNumber, city, postcode, birthday, terms, accumulatorGroup })
    } catch(err) {
      throw err
    }
  },

  async unsubscribeNewsletter({}, { email, accumulatorGroup = null } = {}) {
    try {
      return await service.unsubscribeNewsletter({ email, accumulatorGroup })
    } catch(err) {
      throw err
    }
  },

	async redirect({dispatch}, {name, url, params = {}, hash, force = false} = {}) {
		//TODO:
		const redirectMap = {
			"shop": config.SHOP_URL,
			"shop-taxon": `${config.SHOP_URL}#/taxon={taxon}`,
			"shop-product": `${config.SHOP_URL}#/product={product}`,
			"shop-token": config.SHOP_TOKEN_URL,
			"checkout": config.CHECKOUT_URL,
			"home": config.HOME_URL,
			"home-join-order": config.HOME_JOIN_ORDER_URL,
			"logout-after": config.LOGOUT_CONFIRM_URL,
			"delete-after": config.DELETE_CONFIRM_URL,
			"account": config.ACCOUNT_URL,
			"checkout-token": config.CHECKOUT_TOKEN_URL,
			"checkout-cancel": config.CHECKOUT_CANCEL_URL,
			"checkout-failed": config.CHECKOUT_FAILED_URL,
			"checkout-success": config.CHECKOUT_SUCCESS_URL,
			"checkout-complete": config.CHECKOUT_COMPLETE_URL,
			"checkout-complete-type": config.CHECKOUT_COMPLETE_TYPE_URL,
			"order-feedback-cancel": config.ORDER_FEEDBACK_CANCEL_URL,
			"MIGRATION_URL": config.MIGRATION_URL,
			"MIGRATION_VERIFY_URL": config.MIGRATION_VERIFY_URL,
			"MIGRATION_VERIFY_RESEND_URL": config.MIGRATION_VERIFY_RESEND_URL,
			"reservation": config.RESERVATION_URL,
			"reservation-restaurant": config.RESERVATION_RESTAURANT_URL,
			"reservation-edit": config.RESERVATION_EDIT_URL,
			"indining": config.INDINING_URL,
			"indining-restaurant": config.INDINING_RESTAURANT_URL,
			"custom": '',
		}
		if (redirectMap.hasOwnProperty(name)) {
			//TODO: remove this debug
			let newUrl = url || format(redirectMap[name], params)
			if (!newUrl) {
				return;
			}
			config.DEBUG && console.debug("Redirect: ", name, newUrl)

			// helper for development
			const currentSearch = window.location.search
			if (currentSearch.includes('?_brand') && !newUrl.includes('?')) {
				if (newUrl.includes('#')) {
					const split = newUrl.split('#');
					newUrl = `${split.shift()}${currentSearch}#${split.join('#')}`;
				}
				else {
					newUrl += currentSearch
				}
			}

			hash = hash ? `#${hash}` : '';

			if (window.location.pathname != newUrl || force) {
				dispatch('loader/setLoading', true);
				window.location.href = newUrl + hash
				return true
			} else {
				scrrroll.toTop()
				return false
			}
		}
	},

	async getBrands({state, commit}, { accumulatorGroup = null } = {}) {
		if (state.brandsLoaded) {
			return;
		}
		try {
			let result = await service.brands({ accumulatorGroup })
			commit('setBrands', { item: result.brands.map(brand => ({code: brand})) })
			commit('setBrandsLoaded', { item: true })
		} catch (err) {
			commit('setBrands', { item: [{code: config.BRAND}] })
		}
	}

}
